// レシートのレビューキュー画面
//
// review_status ごとにタブを分け、要確認のものから順に潰していく想定。
// 選択中のレシートは ?id=<receiptId> で保持する（詳細ペインは後続で追加する）。
// 「商品」タブだけは性格が違い、レシートを跨いで買ったものを探すために使う。
// 右下の＋から、レシートをもらえなかった買い物を手で登録できる。
//
// 絞り込み・並び替えはサーバー側で行い、一覧は下端に近づいたら取り足す。
import { format } from 'date-fns'
import { CheckCircle2, Plus, ShoppingBasket, SlidersHorizontal, TriangleAlert } from 'lucide-react'
import { useMemo, useState } from 'react'
import { useLocation, useNavigate, useSearchParams } from 'react-router'

import type { ReceiptReviewStatus, ReceiptSortKey, ReceiptSortOrder } from '@/api/types/receipt'
import { FooterTabBar, type FooterTabSpec } from '@/components/shared/footer-tab-bar'
import { Header } from '@/components/shared/header'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Fab } from '@/components/ui/fab'
import { SearchBox } from '@/components/ui/search-box'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { useGoBack } from '@/hooks/useGoBack'
import { PATHS } from '@/routes/paths'

import { ItemSearchPanel } from './components/item-search-panel'
import { ReceiptCreateSheet } from './components/receipt-create-sheet'
import { ReceiptDetail } from './components/receipt-detail'
import { ReceiptFilterChips } from './components/receipt-filter-chips'
import { ReceiptFilterSheet } from './components/receipt-filter-sheet'
import { ReceiptList } from './components/receipt-list'
import { ReceiptSortMenu } from './components/receipt-sort-menu'
import { useInfiniteReceipts, useReceiptsSummary } from './hooks/use-receipts'
import { emptyReceiptDraft } from './receipt-create-form'
import { type ReceiptFilterFlag } from './receipt-filters'
import {
  activeFilterCount,
  readFilters,
  type ReceiptListFilters,
  shouldReturnOnClose,
  toReceiptsQuery,
  writeFilters,
} from './receipt-query'

/** 商品検索はレビュー状態とは別軸なので、タブの値としてだけ並べる */
const ITEMS_TAB = 'items'
type ReceiptTab = ReceiptReviewStatus | typeof ITEMS_TAB

// 検算も突き合わせも通ったレシートは取り込み時点で確定済みになるため、未確認(pending)は発生しない
const TAB_VALUES = ['needs_review', 'confirmed', ITEMS_TAB] as const satisfies readonly ReceiptTab[]

const TABS: readonly FooterTabSpec<ReceiptTab>[] = [
  { value: 'needs_review', label: '要確認', icon: TriangleAlert },
  { value: 'confirmed', label: '確定済み', icon: CheckCircle2 },
  { value: ITEMS_TAB, label: '商品', icon: ShoppingBasket },
]

const yen = new Intl.NumberFormat('ja-JP')

export const ReceiptListPage = () => {
  const navigate = useNavigate()
  const location = useLocation()
  const goBack = useGoBack(PATHS.RECEIPTS.LIST)
  const [searchParams, setSearchParams] = useSearchParams()
  const [storeNameInput, setStoreNameInput] = useState('')
  const [isFilterOpen, setIsFilterOpen] = useState(false)
  const [isCreateOpen, setIsCreateOpen] = useState(false)
  const storeName = useDebouncedValue(storeNameInput)

  const rawTab = searchParams.get('tab')
  const activeTab = TAB_VALUES.find((value) => value === rawTab) ?? 'needs_review'
  const isItemsTab = activeTab === ITEMS_TAB
  const selectedIdParam = searchParams.get('id')
  const selectedId = selectedIdParam ? Number(selectedIdParam) : null

  const filters = useMemo(() => readFilters(searchParams), [searchParams])
  const selectedFlags = useMemo(() => new Set(filters.flags), [filters.flags])
  const query = useMemo(
    () =>
      toReceiptsQuery(filters, {
        review_status: isItemsTab ? undefined : activeTab,
        store_name: storeName || undefined,
      }),
    [filters, isItemsTab, activeTab, storeName],
  )

  const { data, isPending, isError, error, hasNextPage, isFetchingNextPage, fetchNextPage } = useInfiniteReceipts(
    query,
    { enabled: !isItemsTab },
  )
  const { data: summary } = useReceiptsSummary(query, { enabled: !isItemsTab })

  const receipts = useMemo(() => data?.pages.flat() ?? [], [data])

  // 条件を変えると選択中のレシートが一覧から消えることがあるので、選択も外す
  const updateFilters = (next: ReceiptListFilters) => {
    const params = writeFilters(searchParams, next)
    params.delete('id')
    setSearchParams(params, { replace: true })
  }

  const handleTabChange = (value: ReceiptTab) => {
    const next = new URLSearchParams(searchParams)
    next.delete('id')
    if (value === 'needs_review') {
      next.delete('tab')
    } else {
      next.set('tab', value)
    }
    setSearchParams(next, { replace: true })
  }

  const handleSelect = (receiptId: number) => {
    const next = new URLSearchParams(searchParams)
    next.set('id', String(receiptId))
    // 確定して次へ送った後も、Header の戻るで元の画面へ戻れるよう state を引き継ぐ
    setSearchParams(next, { replace: true, state: location.state })
  }

  const handleToggleFlag = (value: ReceiptFilterFlag) => {
    const flags = filters.flags.includes(value)
      ? filters.flags.filter((flag) => flag !== value)
      : [...filters.flags, value]
    updateFilters({ ...filters, flags })
  }

  const handleSortChange = (sort: ReceiptSortKey, order: ReceiptSortOrder) => {
    updateFilters({ ...filters, sort, order })
  }

  const clearSelection = () => {
    const next = new URLSearchParams(searchParams)
    next.delete('id')
    setSearchParams(next, { replace: true })
  }

  const currentIndex = receipts.findIndex((receipt) => receipt.id === selectedId)
  // 一覧にいない（検索絞り込み中など）なら、絞り込み結果の全件を残りとして扱う
  const totalCount = summary?.total_count ?? receipts.length
  const remainingCount = currentIndex < 0 ? totalCount : totalCount - currentIndex

  // 確定するとそのレシートはタブから外れるので、一覧上の次の行へ送る
  const handleConfirmed = () => {
    const next = receipts.at(currentIndex + 1)
    if (!next) {
      clearSelection()
      return
    }
    handleSelect(next.id)
  }

  if (selectedId !== null) {
    return (
      <div className="flex h-full min-h-0 flex-col">
        <Header title="レシート" onClose={shouldReturnOnClose(location.state) ? goBack : clearSelection} />
        <ReceiptDetail
          receiptId={selectedId}
          remainingCount={remainingCount}
          onClose={clearSelection}
          onConfirmed={handleConfirmed}
        />
      </div>
    )
  }

  const filterCount = activeFilterCount(filters)

  return (
    <div className="flex h-full min-h-0 flex-col">
      <Header title="レシート" onClose={() => navigate(PATHS.HOME)} />

      {isItemsTab ? (
        <ItemSearchPanel onSelectReceipt={handleSelect} />
      ) : (
        <>
          <div className="flex shrink-0 items-center gap-2 p-4 pb-2">
            <SearchBox value={storeNameInput} onChange={(event) => setStoreNameInput(event.target.value)} />
            <ReceiptSortMenu sort={filters.sort} order={filters.order} onChange={handleSortChange} />
            <Button
              variant={filterCount > 0 ? 'default' : 'outline'}
              size="sm"
              className="shrink-0 gap-1 px-2!"
              aria-label="絞り込み"
              onClick={() => setIsFilterOpen(true)}
            >
              <SlidersHorizontal className="size-4" />
              {filterCount > 0 && <span className="text-xs tabular-nums">{filterCount}</span>}
            </Button>
          </div>

          <ReceiptFilterChips summary={summary} selected={selectedFlags} onToggle={handleToggleFlag} />

          {summary && (
            <div className="shrink-0 px-4 pb-2 text-xs text-muted-foreground">
              <Badge variant="outline" className="tabular-nums">
                {yen.format(summary.total_count)}件 ・ 合計 ¥{yen.format(summary.total_amount)}
              </Badge>
            </div>
          )}

          {isError && (
            <div className="mx-4 rounded border border-red-500/40 bg-red-500/10 p-3 text-sm text-red-500">
              {error instanceof Error ? error.message : '読み込みに失敗しました。'}
            </div>
          )}
          {isPending && <div className="px-4 text-sm text-muted-foreground">読み込み中…</div>}
          {!isPending && !isError && receipts.length === 0 && (
            <div className="px-4 text-sm text-muted-foreground">該当するレシートはありません。</div>
          )}

          <ReceiptList
            receipts={receipts}
            selectedId={selectedId}
            onSelect={handleSelect}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            onLoadMore={fetchNextPage}
          />

          <ReceiptFilterSheet
            open={isFilterOpen}
            onOpenChange={setIsFilterOpen}
            filters={filters}
            onChange={updateFilters}
            prefectures={summary?.prefectures ?? []}
          />

          <Fab icon={Plus} label="レシートを手で登録" onClick={() => setIsCreateOpen(true)} />
          <ReceiptCreateSheet
            open={isCreateOpen}
            onOpenChange={setIsCreateOpen}
            initialDraft={emptyReceiptDraft(format(new Date(), 'yyyy-MM-dd'))}
            onCreated={handleSelect}
          />
        </>
      )}

      <FooterTabBar<ReceiptTab>
        idPrefix="receipt-tabs"
        tabs={TABS}
        activeValue={activeTab}
        onChange={handleTabChange}
        ariaLabel="レシートの状態"
      />
    </div>
  )
}
